import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Mail, Phone, Building2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const REQUIRED_FIELDS = ['firstName', 'lastName'];

const CompleteProfile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, company, refreshUserProfile } = useAuth();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const profileRequirements = useMemo(() => {
    return REQUIRED_FIELDS.filter((field) => {
      const value = userData?.[field];
      return !value || !String(value).trim();
    });
  }, [userData]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.userProfile.get();
        const userProfile = response.user || userData || {};
        setFormData({
          firstName: userProfile.firstName || '',
          lastName: userProfile.lastName || '',
          phone: userProfile.phone || '',
          email: userProfile.email || ''
        });
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError(err.getUserFriendlyMessage?.() || 'Unable to load your profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const missingFields = REQUIRED_FIELDS.filter((field) => !formData[field]?.trim());
    if (missingFields.length) {
      setError('Please complete all required fields.');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone?.trim() || undefined
      };

      await api.userProfile.update(payload);
      await refreshUserProfile();
      setSuccess('Your profile has been updated.');

      const redirectTarget = location.state?.redirectTo || '/dashboard';
      navigate(redirectTarget, { replace: true });
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError(err.getUserFriendlyMessage?.() || 'We could not save your profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow rounded-lg p-6 sm:p-10">
          <div className="mb-8 text-center">
            <div className="mx-auto h-14 w-14 flex items-center justify-center rounded-full bg-indigo-50">
              <User className="h-7 w-7 text-indigo-600" />
            </div>
            <h1 className="mt-4 text-2xl font-bold text-gray-900">Complete your profile</h1>
            <p className="mt-2 text-sm text-gray-600">
              Your administrator invited you to {company?.name || 'the company workspace'}. Please share your contact details so the team knows who you are.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">First name<span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Jane"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last name<span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Smith"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone (optional)</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="w-full rounded-md border-gray-300 pl-10 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    placeholder="(+1) 555-123-4567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Work email</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="w-full rounded-md border-gray-200 bg-gray-50 pl-10 text-gray-500"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 flex items-start space-x-3">
              <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="font-medium text-gray-800">Company</p>
                <p>{company?.name || 'Your company'} &middot; {company?.dotNumber ? `DOT ${company.dotNumber}` : 'DOT pending'}</p>
                <p className="text-xs text-gray-500 mt-1">
                  This profile helps your administrators know who joined from their invitation. Only your team can view this information.
                </p>
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4 text-sm text-red-700 flex">
                <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="rounded-md bg-green-50 p-4 text-sm text-green-700 flex">
                <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                <span>{success}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              className="w-full inline-flex justify-center py-2 px-4 border border-transparent rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-60"
            >
              {saving ? 'Saving your profile…' : 'Save and continue'}
            </button>

            {profileRequirements.length > 0 && (
              <p className="text-xs text-red-500 text-center">
                Required fields missing: {profileRequirements.join(', ')}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
